import { useEffect, useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { Check, Loader2, Paperclip } from "lucide-react"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { DeductionSearchResult, searchDeductions } from "@/api/deduction"
import { EmailRemittanceFile, attachEmailFileToDeduction } from "@/api/email_remittance"
import { exactCurrencyFormatter } from "@/utils"
import { toast } from "@/components/ui/use-toast"
import { use_debounced_value } from "@/hooks/use_debounced_value"

interface AttachToDeductionDialogProps {
  file: EmailRemittanceFile | null
  onClose: () => void
  /** Called after a successful attach so the list can refresh its already-parsed badges. */
  onAttached: () => void
}

const SEARCH_DEBOUNCE_MS = 250

/**
 * Attach a remittance file to a deduction that already exists.
 *
 * The third option alongside parsing: plenty of these files are backup for deductions that came in
 * from a portal or an earlier import, where parsing would mint duplicates. The file lands in the
 * deduction's Files, and becomes its backup when it hasn't got one — the server never replaces an
 * existing backup, and says so when it declines.
 */
export function AttachToDeductionDialog({ file, onClose, onAttached }: AttachToDeductionDialogProps) {
  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [attachError, setAttachError] = useState<string | null>(null)

  // Seed the search from the email's subject: a remittance subject usually carries the check or
  // claim number the deduction was booked under, which is the thing worth searching for.
  //
  // Seeding only sets state; the fetch belongs to the swr key below. Kicking off a load here as
  // well used to fire a second, identical request 250 ms behind the first, and whichever landed
  // last won.
  useEffect(() => {
    if (!file) return
    setQuery(longest_number(file.subject) ?? "")
    setSelected(null)
    setAttachError(null)
  }, [file?.attachment_id])

  const debounced_query = use_debounced_value(query, SEARCH_DEBOUNCE_MS)
  const {
    data: results,
    error: searchError,
    isLoading: loading,
  } = useSWR<DeductionSearchResult[]>(
    file ? `/deduction/search?q=${encodeURIComponent(debounced_query)}&exclude_repayments=1` : null,
    () => searchDeductions({ q: debounced_query || undefined, exclude_repayments: true })
  )
  const error = attachError ?? (searchError ? "Could not search deductions" : null)

  async function handleAttach() {
    if (!file || !selected) return
    setSaving(true)
    setAttachError(null)
    try {
      const result = await attachEmailFileToDeduction(file.attachment_id, selected)
      toast({
        title: result.set_as_backup
          ? `Attached as backup to ${result.invoice_number}`
          : `Attached to ${result.invoice_number}`,
        description: result.note ?? undefined,
      })
      onAttached()
      onClose()
    } catch (e) {
      setAttachError(e instanceof Error ? e.message : "Could not attach the file")
    } finally {
      setSaving(false)
    }
  }

  if (!file) return null
  const rows = results ?? []

  return (
    <Dialog open={file !== null} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Attach to an existing deduction</DialogTitle>
          <p class="text-sm text-gray-500 truncate">
            {file.name} — {file.subject}
          </p>
        </DialogHeader>

        <Input
          value={query}
          placeholder="Search by invoice, check or PO number"
          className="shrink-0"
          onInput={(e: Event) => setQuery((e.target as HTMLInputElement).value)}
        />

        <div class="flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-200">
          {loading ? (
            <div class="p-6 text-center text-gray-500 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching…
            </div>
          ) : rows.length === 0 ? (
            <div class="p-6 text-center text-gray-500">No deductions match that search.</div>
          ) : (
            rows.map(d => (
              <button
                key={d.id}
                type="button"
                class={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 ${
                  selected === d.id ? "bg-blue-50" : ""
                }`}
                onClick={() => setSelected(d.id)}>
                <Check
                  className={`h-4 w-4 mt-0.5 shrink-0 ${selected === d.id ? "text-blue-600" : "text-transparent"}`}
                />
                <span class="min-w-0 flex-1">
                  <span class="flex items-center gap-2 flex-wrap">
                    <span class="font-medium text-gray-900">{d.invoice_number}</span>
                    <span class="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{d.source}</span>
                    <span class="text-xs text-gray-500">{d.kind}</span>
                  </span>
                  <span class="block text-sm text-gray-600 mt-0.5">
                    {exactCurrencyFormatter(d.invoice_amount)}
                    {d.invoice_date ? ` · ${format(parseISO(d.invoice_date), "MMM d, yyyy")}` : ""}
                    {d.customer_name ? ` · ${d.customer_name}` : ""}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>

        {error && <p class="text-sm text-red-600">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button className="gap-1.5" disabled={!selected || saving} onClick={() => void handleAttach()}>
            <Paperclip className="h-4 w-4" />
            {saving ? "Attaching…" : "Attach as backup"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The longest run of digits in a subject — usually the check or claim number.
 *
 * Five digits minimum, not four: real references run long (`11422`, `1116220`, `0004153298`) while
 * every four-digit run in these subjects is a year. Seeding "2026" from "Payment Advice Note from
 * 07/23/2026" would open the dialog on a search matching most of the ledger, which is worse than
 * opening on nothing and letting the user type what they know.
 */
export function longest_number(subject: string): string | null {
  const runs = subject.match(/\d{5,}/g)
  if (!runs || runs.length === 0) return null
  return runs.reduce((longest, run) => (run.length > longest.length ? run : longest))
}
